import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  TemplateRef,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { MomentModule } from "ngx-moment";
import { LazyLoadEvent, SharedModule } from "primeng/api";
import { DatePickerModule } from "primeng/datepicker";
import { TableModule } from "primeng/table";
import { EmptyStateComponent } from "src/app/design-system/components/empty-state/empty-state.component";
import { AccountantService } from "../main/accountant/accountant.service";
import { environment } from "src/environments/environment";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { HotToastService } from "@ngneat/hot-toast";
import { DashboardService } from "../dashboard.service";
import { ActiveClientsOnlyPipe } from "../main/common/active-clients-only.pipe";
import { DashboardStore } from "../dashboard.store";

@Component({
  selector: "app-invites-dashboard",
  imports: [
    CommonModule,
    BsDropdownModule,
    DatePickerModule,
    FormsModule,
    TableModule,
    SharedModule,
    TooltipModule,
    MomentModule,
    EmptyStateComponent,
    ActiveClientsOnlyPipe,
  ],
  templateUrl: "./invites-dashboard.component.html",
  styleUrls: [
    "./invites-dashboard.component.scss",
    "./../p-table-dashboards.scss",
  ],
})
export class InvitesDashboardComponent implements OnInit {
  loading = false;
  lastLazyEvent: LazyLoadEvent;
  totalRecords: number = 0;
  dashboardName = "invitesDashboard";
  cols = [
    { field: "workspace", header: "Workspace" },
    { field: "name", header: "Name" },
    { field: "email", header: "Email" },
    { field: "created", header: "Created" },
  ];
  selectedInvites = [];
  invites = [];
  modalRef: BsModalRef;

  initialFilters = {
    invitesDashboard: {
      workspaceFilter: {
        label: "All Workspaces",
        value: null,
      },
      sortOrder: -1,
      sortBy: "created",
      rows: 100,
    },
  };

  modalService = inject(BsModalService);
  cdr = inject(ChangeDetectorRef);
  accountantService = inject(AccountantService);
  toastr = inject(HotToastService);
  public dashboardService = inject(DashboardService);
  dashboardStore = inject(DashboardStore);

  sortField = null;
  sortOrder = null;

  ngOnInit(): void {
    if (!this.dashboardService.filters()[this.dashboardName]) {
      this.dashboardService.filters.update((prevState = {}) => ({
        ...prevState,
        [this.dashboardName]: {
          ...this.initialFilters.invitesDashboard,
        },
      }));
    }
    this.sortField =
      this.dashboardService.filters()[this.dashboardName]?.sortBy;
    this.sortOrder =
      this.dashboardService.filters()[this.dashboardName]?.sortOrder;
  }

  changeWorkspaceFilter(label: string, value: string | null) {
    this.dashboardService.filters.update((prevState) => ({
      ...prevState,
      [this.dashboardName]: {
        ...prevState[this.dashboardName],
        workspaceFilter: { label, value },
      },
    }));
    this.getDashboardData();
  }

  changeSortByFilter(sortBy: string, sortOrder: number) {
    if (!sortBy) return;
    const {
      [this.dashboardName]: { sortOrder: currentSortOrder },
    } = this.dashboardService.filters();
    let newSortOrder = currentSortOrder === 1 ? -1 : sortOrder;
    this.dashboardService.filters.update((prevState) => ({
      ...prevState,
      [this.dashboardName]: {
        ...prevState[this.dashboardName],
        sortOrder: newSortOrder,
        sortBy,
      },
    }));
  }

  async getDashboardData(event: LazyLoadEvent = null) {
    this.loading = true;
    if (event) {
      this.lastLazyEvent = event;
    }
    const {
      [this.dashboardName]: { workspaceFilter, sortBy, sortOrder },
    } = this.dashboardService.filters();

    this.accountantService
      .get_clients_invites(
        this.lastLazyEvent?.first,
        this.lastLazyEvent?.rows,
        sortBy,
        sortOrder,
        workspaceFilter,
      )
      .then(({ invites, total_count }: any) => {
        console.log({ invites, total_count });
        this.invites = invites;
        this.totalRecords = total_count;
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  copyInviteLink() {
    const [invite] = this.selectedInvites;
    const url = `${environment.frontUrl}registration/join/${invite.uuid}`;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        console.log("Invite link copied successfully!");
      })
      .catch((err) => {
        console.error("Could not copy invite link: ", err);
      });
  }

  removeInvites(template: TemplateRef<any>): void {
    this.modalRef = this.modalService.show(template, { class: "modal-sm" });
  }

  getSelectedInvitesIds() {
    return this.selectedInvites.map((invite) => invite.id).join(",");
  }

  async confirmInvitesRemoval() {
    const { status, message } = await this.accountantService.remove_invite(
      this.getSelectedInvitesIds(),
    );
    if (status === 200) {
      this.toastr.success(message);
    } else {
      this.toastr.error(message);
    }
    // this.getDashboardData(this.lastLazyEvent);
    this.selectedInvites = [];
    this.modalRef.hide();
  }

  resendInvites() {
    this.dashboardService.show_loading.next(true);
    this.accountantService.send_invite_email(this.getSelectedInvitesIds()).then(
      (res: any) => {
        this.dashboardService.show_loading.next(false);
        this.toastr.success(res.message);
        this.selectedInvites = [];
      },
      (err) => {
        this.dashboardService.show_loading.next(false);
        this.toastr.error(err.error.message);
      },
    );
  }
}
